import React, { useState, useCallback } from "react"
import {
  Modal,
  ModalBody,
  ModalHeader,
  Row,
  Col,
  Label,
  Input,
  FormFeedback,
  Button,
  Form,
} from "reactstrap"
import * as Yup from "yup"
import { useFormik } from "formik"
import { toast } from "react-toastify"
import { useEditVehicleStock } from "hooks/stockHook"
import ButtonTooltip from "components/Common/ButtonTooltip"
import { useToastMessage } from "hooks/useToastMessage"

export default function StockPricingUpdate({
  stock = null,
  btnId = "edit-stock-pricing",
}) {
  const [modal, setModal] = useState(false)
  const toggle = () => setModal(!modal)

  const { handleResponse } = useToastMessage()

  const { mutateAsync: editStock, isPending: stockEditLoading } =
    useEditVehicleStock()

  const validationSchema = Yup.object({
    asking_price: Yup.number()
      .min(0, "Asking price must be positive")
      .required("Asking price is required"),
    discount_price: Yup.number()
      .min(0, "Discount price must be positive")
      .test(
        "less-than-asking",
        "Discount price cannot be higher than asking price",
        function (value) {
          return !value || value <= this.parent.asking_price
        }
      ),
  })

  const onSubmit = useCallback(
    async (values, { resetForm }) => {
      try {
        const res = await editStock({ id: stock?.id, ...values })
        handleResponse(res, "Pricing updated successfully.", true)
        if (res.success) {
          toggle()
          resetForm()
        }
      } catch (error) {
        console.error("Failed to update pricing:", error)
      }
    },
    [editStock, stock, handleResponse]
  )

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      asking_price: stock?.asking_price || "",
      discount_price: stock?.discount_price || "",
    },
    validationSchema,
    onSubmit,
  })

  return (
    <>
      <ButtonTooltip
        type="edit"
        onClick={toggle}
        btnId={btnId}
        btnName="Edit Pricing"
      />

      <Modal isOpen={modal} toggle={toggle} centered>
        <ModalHeader toggle={toggle}>Edit Pricing Information</ModalHeader>
        <ModalBody>
          <Form
            onSubmit={e => {
              e.preventDefault()
              validation.handleSubmit()
            }}
          >
            <Row>
              <Col md={12}>
                <Label>Asking Price</Label>
                <Input
                  type="number"
                  name="asking_price"
                  value={validation.values.asking_price}
                  onChange={validation.handleChange}
                  invalid={!!validation.errors.asking_price}
                />
                <FormFeedback>{validation.errors.asking_price}</FormFeedback>
              </Col>
            </Row>

            <Row className="mt-3">
              <Col md={12}>
                <Label>Discount Price</Label>
                <Input
                  type="number"
                  name="discount_price"
                  value={validation.values.discount_price}
                  onChange={validation.handleChange}
                  invalid={!!validation.errors.discount_price}
                />
                <FormFeedback>{validation.errors.discount_price}</FormFeedback>
              </Col>
            </Row>

            <div className="text-end mt-4">
              <Button type="submit" color="success" disabled={stockEditLoading}>
                Save Changes
                {stockEditLoading && "..."}
              </Button>
            </div>
          </Form>
        </ModalBody>
      </Modal>
    </>
  )
}
